import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const HrDashboardIndex = () => {
  // Sample data for metrics and charts - in a real app, this would come from an API
  const metrics = {
    totalEmployees: 248,
    newHires: 12,
    openPositions: 8,
    upcomingReviews: 15,
    attendanceRate: 97.2,
    leaveRequests: 7,
    pendingApprovals: 5,
    trainingCompliance: 92.5,
    turnoverRate: 3.1,
  };

  const recentActivity = [
    { id: 1, type: 'Leave Request', user: 'Sarah Johnson', status: 'Pending', time: '2 hours ago' },
    { id: 2, type: 'New Hire', user: 'Michael Chen', status: 'Completed', time: '1 day ago' },
    { id: 3, type: 'Training', user: 'David Williams', status: 'Scheduled', time: '3 days ago' },
    { id: 4, type: 'Loan Request', user: 'Priya Sharma', status: 'Approved', time: '1 week ago' },
  ];

  const [expandedCard, setExpandedCard] = useState(null);

  const hrSubpages = [
    {
      id: 'onboard',
      title: 'Onboard Staff',
      route: '/staff-onboard',
      description: 'Guide new employees through the onboarding process',
      metrics: [
        { label: 'In progress', value: '4' },
        { label: 'This month', value: '12' },
      ],
      quickActions: ['Start onboarding', 'Update checklist'],
      icon: (
        <svg className="w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0-3h3m-3 0h-3m0 3v3m0-3v-3m-5 2a4 4 0 100-8 4 4 0 000 8z" />
        </svg>
      ),
      bgColor: 'bg-primary-400'
    },
    {
      id: 'attendance',
      title: 'Attendance',
      route: '/attendance',
      description: 'View and manage attendance records',
      metrics: [
        { label: 'Today\'s Rate', value: '97.2%' },
        { label: 'Absences', value: '5' },
      ],
      quickActions: ['Mark attendance', 'Run report'],
      icon: (
        <svg className="w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ),
      bgColor: 'bg-primary-600'
    },
    {
      id: 'leave',
      title: 'Leave Management',
      route: '/hr/leave',
      description: 'Review and approve leave requests',
      metrics: [
        { label: 'Pending', value: '7' },
        { label: 'Approved', value: '12' },
      ],
      quickActions: ['Approve requests', 'Create policy'],
      icon: (
        <svg className="w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10m-10 4h10m-10 4h10" />
        </svg>
      ),
      bgColor: 'bg-primary-700'
    },
    {
      id: 'loan',
      title: 'Staff Loans',
      route: '/hr/loan',
      description: 'Process loan applications and track repayments',
      metrics: [
        { label: 'Pending', value: '3' },
        { label: 'Active', value: '15' },
      ],
      quickActions: ['Review applications', 'Payment summary'],
      icon: (
        <svg className="w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2-1.343-2-3-2z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6" />
        </svg>
      ),
      bgColor: 'bg-primary-500'
    },
    {
      id: 'training',
      title: 'Training',
      route: '/hr/training',
      description: 'Plan, track, and report on employee training activities',
      metrics: [
        { label: 'Upcoming', value: '5' },
        { label: 'Compliance', value: '92.5%' },
      ],
      quickActions: ['Schedule session', 'Assign course'],
      icon: (
        <svg className="w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 20h9" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4H3v16h9" />
        </svg>
      ),
      bgColor: 'bg-primary-800'
    },
    {
      id: 'reports',
      title: 'Reports',
      route: '/hr/reports',
      description: 'Generate custom reports and analytics',
      metrics: [
        { label: 'Saved', value: '24' },
        { label: 'Recent', value: '5' },
      ],
      quickActions: ['Run report', 'Export data'],
      icon: (
        <svg className="w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      bgColor: 'bg-primary-600'
    }
  ];

  // Sample chart data - this would be real data in a production app
  const AttendanceChart = () => (
    <div className="h-32 bg-white p-4 rounded-lg shadow-sm flex items-center justify-center">
      <div className="flex w-full h-full items-end justify-between px-2">
        <div className="flex flex-col items-center">
          <div className="bg-primary-500 w-6 rounded-t-sm" style={{height: `${95*0.3}%`}}></div>
          <span className="text-xs mt-1">Mon</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="bg-primary-500 w-6 rounded-t-sm" style={{height: `${97*0.3}%`}}></div>
          <span className="text-xs mt-1">Tue</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="bg-primary-500 w-6 rounded-t-sm" style={{height: `${98*0.3}%`}}></div>
          <span className="text-xs mt-1">Wed</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="bg-primary-500 w-6 rounded-t-sm" style={{height: `${96*0.3}%`}}></div>
          <span className="text-xs mt-1">Thu</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="bg-primary-500 w-6 rounded-t-sm" style={{height: `${97*0.3}%`}}></div>
          <span className="text-xs mt-1">Fri</span>
        </div>
      </div>
    </div>
  );

  const MetricCard = ({ title, value, change, icon, isPositive = true }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="bg-white p-4 rounded-xl shadow-sm"
    >
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-sm font-medium text-gray-500">{title}</h3>
          <p className="text-2xl font-bold mt-1 text-gray-900">{value}</p>
          {change && (
            <p className={`text-xs mt-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {isPositive ? '↑' : '↓'} {change} from last month
            </p>
          )}
        </div>
        <div className="p-2 rounded-lg bg-primary-100 text-primary-600">
          {icon}
        </div>
      </div>
    </motion.div>
  );

  const QuickAction = ({ label, onClick, icon }) => (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className="flex items-center py-2 px-3 bg-primary-50 hover:bg-primary-100 rounded-lg text-primary-700 text-sm font-medium"
      onClick={onClick}
    >
      {icon}
      <span className="ml-2">{label}</span>
    </motion.button>
  );

  return (
    <div className="flex min-h-screen bg-gray-50 source-sans-3">
      <div className="flex-1 p-4 md:p-8">
        {/* Header with Quick Stats */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center justify-between">
            <div>
                <h2 className="text-2xl font-bold text-primary-800 source-sans-3">Welcome back, John</h2>
                <p className="text-primary-600 mt-1 source-sans-3">Let's dive into your staff statistics today.</p>
            </div>
            <div className="flex space-x-3 mt-4 sm:mt-0">
              <QuickAction 
                label="Add Employee" 
                icon={
                  <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                }
              />
              <QuickAction
                label="View Reports" 
                icon={
                  <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                }
              />
            </div>
          </div>
        </div>

        {/* Key Metrics Section */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Key Metrics</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard 
              title="Total Employees" 
              value={metrics.totalEmployees}
              change="3.2%"
              icon={
                <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              }
            />
            <MetricCard
              title="Attendance Rate" 
              value={`${metrics.attendanceRate}%`}
              change="0.5%"
              icon={
                <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
            <MetricCard
              title="Pending Approvals" 
              value={metrics.pendingApprovals}
              change="12%"
              isPositive={false}
              icon={
                <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              }
            />
            <MetricCard
              title="Training Compliance" 
              value={`${metrics.trainingCompliance}%`}
              change="1.8%"
              icon={
                <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              }
            />
          </div>
        </div>

        {/* HR Functions */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">HR Functions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {hrSubpages.map((page, i) => (
              <motion.div
                key={page.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
                className="bg-white rounded-xl shadow-sm overflow-hidden relative"
                onClick={() => setExpandedCard(expandedCard === page.id ? null : page.id)}
              >
                <div className="p-5">
                  <div className="flex items-center">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${page.bgColor}`}>
                      {page.icon}
                    </div>
                    <div className="ml-4 flex-1">
                      <h2 className="text-lg font-semibold text-gray-900">{page.title}</h2>
                      <p className="text-sm text-gray-500 mt-1">{page.description}</p>
                    </div>
                    <div className="text-gray-400">
                      <svg className={`w-5 h-5 transform ${expandedCard === page.id ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  
                  {/* Expandable Content */}
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ 
                      height: expandedCard === page.id ? 'auto' : 0,
                      opacity: expandedCard === page.id ? 1 : 0
                    }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden mt-4"
                  >
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      {page.metrics.map((metric, i) => (
                        <div key={i} className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-500">{metric.label}</p>
                          <p className="text-lg font-semibold text-gray-900">{metric.value}</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 mt-3">
                      {page.quickActions.map((action, i) => (
                        <button key={i} className="text-xs py-1 px-3 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-md">
                          {action}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                </div>
                
                {/* Go to page link - always visible */}
                <Link to={page.route}>
                  <div className="bg-gray-50 p-3 flex justify-between items-center border-t border-gray-100">
                    <span className="text-sm text-primary-600 font-medium">View {page.title}</span>
                    <svg className="w-4 h-4 text-primary-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Overview Charts */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Overview</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-xl shadow-sm lg:col-span-2">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Weekly Attendance</h3>
              <AttendanceChart />
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Recent Activity</h3>
              <div className="space-y-3 mt-4">
                {recentActivity.map(activity => (
                  <div key={activity.id} className="flex items-center py-2 border-b border-gray-100">
                    <div className={`w-2 h-2 rounded-full ${
                      activity.status === 'Pending' ? 'bg-yellow-400' : 
                      activity.status === 'Completed' ? 'bg-green-500' :
                      activity.status === 'Approved' ? 'bg-green-500' : 'bg-blue-500'
                    } mr-2`} />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">{activity.type}</p>
                      <p className="text-xs text-gray-500">{activity.user}</p>
                    </div>
                    <div className="text-xs text-gray-400">{activity.time}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HrDashboardIndex;